<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Stats -->
        <v-row class="stats mt-4" dense>
          <v-col cols="12" sm="3">
            <StatCard title="All Admin" :value="totalAdminsCount" icon="mdi-account-multiple-outline"
              iconColor="#22c55e" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Active Admin" :value="activeAdminCount" icon="mdi-account-lock-outline"
              iconColor="#ef4444" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Inactive Admin" :value="inactiveAdminCount" icon="mdi-account-check-outline"
              iconColor="#22c55e" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Super Admin" :value="superAdminCount" icon="mdi-account-multiple-remove-outline"
              iconColor="#ef4444" />
          </v-col>
        </v-row>

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <div class="table-header d-flex justify-space-between align-center flex-wrap ga-3">
            <div class="d-flex align-center flex-wrap ga-3">
              <!-- Left side: search + clear -->
              <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
                <InputText v-model="filters['global'].value" placeholder="Search Users..." />
                <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined"
                  @click="clearFilters" />
              </div>
            </div>

            <!-- Right side: Save Admin button -->
            <button type="button" :disabled="loading" class="theme-button px-5"
              @click="resetForm(); isEditing = false; editingId = null; adminDialog = true">
              <span v-if="!loading"><v-icon start>mdi-content-save-cog-outline</v-icon> Save Admin </span>
            </button>
          </div>

          <!-- PrimeVue DataTable with search -->
          <DataTable v-model:filters="filters" :value="admins" paginator :rows="10" dataKey="uuid" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 50rem"
            filterDisplay="menu" selectionMode="single"
            :globalFilterFields="['profile.account_id', 'profile.firstname', 'profile.lastname', 'profile.username', 'status']">

            <!-- Empty table -->
            <template #empty>
              No Admin found.
            </template>

            <!-- Loading state -->
            <template #loading>
              Loading Admins...
            </template>

            <!-- Product Column -->
            <Column header="Name">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    <div class="product-name">{{ slotProps.data.profile.firstname }} {{ slotProps.data.profile.lastname
                      }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!--User Column-->
            <Column header="Email">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    <div class="product-name">{{ slotProps.data.email }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!--Amount Column-->
            <Column header="Username">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ slotProps.data.profile.username }}
                  </div>
                </div>
              </template>
            </Column>

            <!-- Status Column -->
            <Column header="Status">
              <template #body="slotProps">
                <span :class="[
                  'status-pill',
                  slotProps.data.account_status === 'active'
                    ? 'status-active'
                    : 'status-inactive'
                ]">
                  {{ slotProps.data.account_status === 'active' ? 'Active' : 'Inactive' }}
                </span>
              </template>
            </Column>


            <!--Type Column-->
            <Column header="Role">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ formatRole(slotProps.data.role?.[0]) }}
                  </div>
                </div>
              </template>
            </Column>

            <!-- Created Column -->
            <Column header="Added At">
              <template #body="slotProps">
                {{ formatDateTime(slotProps.data.created_at) }}
              </template>
            </Column>

            <!-- Actions Column -->
            <Column header="" style="width:120px; border-radius: 10px;">
              <template #body="slotProps">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn icon v-bind="props" variant="text">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="openEditModal(slotProps.data)">
                      Edit Details
                    </v-list-item>
                  </v-list>

                </v-menu>
              </template>
            </Column>

          </DataTable>
        </v-card>
        <!--  Modal -->
        <v-dialog v-model="adminDialog" max-width="650">
          <v-card class="pa-6">

            <v-card-title class="text-h6 mb-4 text-center">
              {{ isEditing ? 'Edit Admin' : 'Create Admin' }}
            </v-card-title>

            <form @submit.prevent="handleCreateAdmin" class="space-y-5">

              <!-- Firstname Name -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Firstname</label>
                <v-text-field v-model="form.firstname" type="text" variant="outlined" placeholder="Firstname"
                  :rules="[v => !!v || 'Firstname is required']" />
              </div>

              <!-- Last Name -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Lastname</label>
                <v-text-field v-model="form.lastname" type="text" variant="outlined" placeholder="Lastname"
                  :rules="[v => !!v || 'Lastname is required']" />
              </div>

              <!-- Email Name -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Email</label>
                <v-text-field v-model="form.email" type="email" variant="outlined" placeholder="Email"
                  :rules="[v => !!v || 'Email is required']" />
              </div>

              <!--  Username -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Username</label>
                <v-text-field v-model="form.username" type="text" variant="outlined" placeholder="Username"
                  :rules="[v => !!v || 'Username is required']" />
              </div>



              <div class="flex flex-col gap-2">
                <v-select v-model="form.role" :items="allRoles" item-title="name" item-value="name" label="Role"
                  variant="outlined" :rules="[v => !!v || 'Role is important']" />
              </div>

              <v-card v-if="form.password" variant="tonal" class="pa-3 mt-3">
                <div class="d-flex justify-space-between align-center">

                  <!-- Password -->
                  <div>
                    <div class="text-caption text-grey mb-1">
                      Generated Password
                    </div>

                    <div class="d-flex align-center ga-1">
                      <span class="text-body-1 font-weight-bold">
                        {{ form.password }}
                      </span>

                      <v-btn :icon="copied ? 'mdi-check' : 'mdi-content-copy'" variant="text" density="compact"
                        size="x-small" color="primary" @click="copyPassword" />
                    </div>
                  </div>

                  <!-- Regenerate -->
                  <v-btn icon="mdi-refresh" variant="text" density="comfortable" color="orange"
                    @click="regeneratePassword" />
                </div>
              </v-card>

              <!-- ACTIONS -->
              <div class="flex justify-center align-center  gap-3 pt-4"
                style="display:flex; justify-content:center; align-items:center; gap:12px; padding-top:16px;">
                <v-btn variant="text" @click="adminDialog = false">
                  Cancel
                </v-btn>
                <v-btn color="black" type="submit" :loading="adminLoading" :disabled="!isFormValid">
                  {{ isEditing ? 'Update Admin' : 'Save Admin' }}
                </v-btn>
              </div>

            </form>
          </v-card>
        </v-dialog>
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right"> {{
          snackbar.message }}
          <template #actions>
            <v-btn variant="outlined" @click="snackbar.show = false">
              Cancel
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import { adminStore } from "@/store/admin"
import StatCard from "@/components/StatCard.vue"
import { formatDateTime, formatRole, getAllErrors } from "@/util"
import { executeCreateAdmin, executeUpdateAdmin } from "@/api/admin"
//import { useRouter } from "vue-router"


const adm = adminStore()
//const router = useRouter()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const filters = ref({ global: { value: null, matchMode: "contains" } })
const adminDialog = ref(false)
const adminLoading = ref(false)

const isEditing = ref(false)
const editingId = ref(null)
const copied = ref(false)

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

const allRoles = computed(() => adm.roles || [])

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  try {
    await adm.loadAdmins()
    await adm.loadRoles()
  } catch (error) {
    console.error(error)
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

const admins = computed(() => adm.admins || [])

const totalAdminsCount = computed(() => admins.value.length)
const activeAdminCount = computed(() => admins.value.filter(admin => admin.account_status === 'active').length || 0)
// const totalCompliantUsers = computed(() => usr.users?.compliant_users || 0)
const inactiveAdminCount = computed(() => admins.value.filter(admin => admin.account_status === 'inactive').length || 0)
const superAdminCount = computed(() =>
  admins.value?.filter(admin =>
    Array.isArray(admin.role) &&
    admin.role.includes('super-admin')
  ).length || 0
)


/* Clear filters */
const clearFilters = () => {
  filters.value = { global: { value: null, matchMode: "contains" } }
}

const snackbar = ref({
  show: false,
  message: "",
  color: "success" // success | error | warning | info
})

const showSnackbar = (message, color = "success") => {
  snackbar.value.message = message
  snackbar.value.color = color
  snackbar.value.show = true
}

const form = ref({
  firstname: "",
  lastname: "",
  email: "",
  username: "",
  role: "",
  password: ""
})

/**
 * FORM VALIDATION STATE
 */
const isFormValid = computed(() => {
  return !!(form.value.firstname && form.value.lastname && form.value.email && form.value.username && form.value.role)
})

/**
 * RESET FORM
 */
const resetForm = () => {
  editingId.value = null;
  isEditing.value = false;
  form.value = {
    firstname: "",
    lastname: "",
    email: "",
    username: "",
    role: "",
    password: ""
  }
}

//editing modal
const openEditModal = (item) => {
  isEditing.value = true
  editingId.value = item.id

  form.value.firstname = item.profile?.firstname || ""
  form.value.lastname = item.profile?.lastname || ""
  form.value.email = item.email || ""
  form.value.username = item.profile?.username || ""
  form.value.role = item.role || ""
  adminDialog.value = true
}


watch(
  () => form.value.role,
  (role) => {
    if (role && !isEditing.value && !form.value.password) {
      form.value.password = generatePassword()
    }
  }
)

//Function to create or edit
const handleCreateAdmin = async () => {
  adminLoading.value = true

  try {

    const payload = new FormData()

    payload.append("firstname", form.value.firstname)
    payload.append("lastname", form.value.lastname)
    payload.append("email", form.value.email)
    payload.append("username", form.value.username)
    payload.append("role", form.value.role)
    payload.append("password", form.value.password)

    if (isEditing.value) {
      // update api
      await executeUpdateAdmin(editingId.value, payload)
      showSnackbar("Admin updated successfully")

    } else {
      // create api
      await executeCreateAdmin(payload)
      showSnackbar("Admin created successfully")

    }

    await adm.loadAdmins()
    //close the modal

    //resetForm()

  } catch (e) {


    const msg = getAllErrors(e)
    showSnackbar(
      msg || "Failed to save admin",
      "error"
    )
  } finally {
    adminLoading.value = false
  }
}

//generate random password
function generatePassword(length = 12) {
  const chars =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%'

  return Array.from({ length }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('')
}

function regeneratePassword() {
  form.value.password = generatePassword()
}

async function copyPassword() {
  try {
    await navigator.clipboard.writeText(form.value.password)

    copied.value = true

    setTimeout(() => {
      copied.value = false
    }, 3000)
  } catch (e) {
    console.error(e)
  }
}
</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}

/* Status */
.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

/* Table */
.modern-table {
  border: none;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody>tr {
  cursor: pointer;
}

.p-datatable-tbody>tr:hover {
  background-color: #f5f5f5;
}
</style>
